import React, { useLayoutEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import TailwindModal from '../ui/TailwindModal'
import { highlightCost } from '../modalCostHighlight'

// Shared palette first — EcsPackAlert.css reads its tokens from :root, keeping
// this in step with the confirm modal and the deposit/notification alerts.
import '../modalTheme.css'
import './EcsPackAlert.css'

/** Must match the exit animation duration in EcsPackAlert.css. */
const EXIT_MS = 140

interface EcsPackAlertState {
  visible: boolean
  text: string
  resolve?: () => void
}

let showEcsPackAlert: ((text: string) => Promise<void>) | undefined

export const EcsPackAlertHost: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [alertState, setAlertState] = useState<EcsPackAlertState>({
    visible: false,
    text: '',
  })

  useLayoutEffect(() => {
    showEcsPackAlert = (text: string) =>
      new Promise<void>((resolve) => {
        setAlertState({ visible: true, text, resolve })
      })

    return () => {
      showEcsPackAlert = undefined
    }
  }, [])

  const handleClose = () => {
    setAlertState((previousState) => {
      previousState.resolve?.()
      // Keep `text` so the message stays put while the modal animates out.
      return { ...previousState, visible: false, resolve: undefined }
    })
  }

  const handleBuyEcs = () => {
    handleClose()
    navigate('/shop/gem-pack')
  }

  return (
    <TailwindModal
      open={alertState.visible}
      onClose={handleClose}
      zIndex={9999}
      exitMs={EXIT_MS}
      aria-labelledby="ecs-pack-alert-title"
      aria-describedby="ecs-pack-alert-desc"
      backdropClassName="ecs-pack-alert-backdrop"
    >
      <section className="ecs-pack-alert-modal" role="dialog" aria-modal="true">
        <header className="ecs-pack-alert-modal__header">
          <h2 id="ecs-pack-alert-title" className="ecs-pack-alert-modal__title">
            {t('alert.insufficientEcsTitle')}
          </h2>

          <button
            className="ecs-pack-alert-modal__close"
            type="button"
            aria-label={t('common.close', { defaultValue: 'Close' })}
            onClick={handleClose}
          />
        </header>

        <div className="ecs-pack-alert-modal__content">
          <p id="ecs-pack-alert-desc" className="ecs-pack-alert-modal__message">
            {highlightCost(alertState.text, 'ecs-pack-alert-modal__cost')}
          </p>
        </div>

        <div className="ecs-pack-alert-modal__ornament" aria-hidden="true">
          <span className="ecs-pack-alert-modal__ornament-line" />
          <span className="ecs-pack-alert-modal__ornament-center">
            <i className="ecs-pack-alert-modal__ornament-dot ecs-pack-alert-modal__ornament-dot--left" />
            <i className="ecs-pack-alert-modal__ornament-diamond ecs-pack-alert-modal__ornament-diamond--left-outer" />
            <i className="ecs-pack-alert-modal__ornament-diamond ecs-pack-alert-modal__ornament-diamond--left-inner" />
            <i className="ecs-pack-alert-modal__ornament-diamond ecs-pack-alert-modal__ornament-diamond--main" />
            <i className="ecs-pack-alert-modal__ornament-diamond ecs-pack-alert-modal__ornament-diamond--right-inner" />
            <i className="ecs-pack-alert-modal__ornament-diamond ecs-pack-alert-modal__ornament-diamond--right-outer" />
            <i className="ecs-pack-alert-modal__ornament-dot ecs-pack-alert-modal__ornament-dot--right" />
          </span>
          <span className="ecs-pack-alert-modal__ornament-line" />
        </div>

        <footer className="ecs-pack-alert-modal__actions">
          <button
            className="ecs-pack-alert-modal__button ecs-pack-alert-modal__button--cancel"
            type="button"
            onClick={handleClose}
          >
            {t('common.ok')}
          </button>
          <button
            className="ecs-pack-alert-modal__button"
            type="button"
            onClick={handleBuyEcs}
            autoFocus
          >
            {t('alert.buyEcs')}
          </button>
        </footer>

        <p className="ecs-pack-alert-modal__hint">{t('alert.insufficientEcsBuyHint')}</p>
      </section>
    </TailwindModal>
  )
}

export const ecsPackAlert = {
  open: async (text: string) => {
    if (!showEcsPackAlert) {
      console.warn('ECS pack alert not initialized yet.')
      return
    }
    await showEcsPackAlert(text)
  },
}
